<template>
  <FormPage
    title="Машины"
    description="Список всех машин в системе"
  >
    <template #controls>
      <button
        class="btn btn-primary"
        type="button"
        @click="router.push({ name: 'CarCreate' })"
      >+ Создать машину</button>
    </template>

    <FormCard>
      <FormGrid :cols="3">
        <FormField label="Общий поиск">
          <input
            v-model.trim="filters.query"
            type="text"
            placeholder="Гос Номер, Номер телефона клиента, VIN-номер"
            @update:model-value="onFilterEdited"
          />
        </FormField>

        <FormField label="Поиск по номеру Авто">
          <input
            v-model.trim="filters.queryCarNumber"
            type="text"
            placeholder="Номер Авто"
            @update:model-value="onFilterEdited"
          />
        </FormField>

        <FormField label="Поиск по VIN-номеру">
          <input
            v-model.trim="filters.queryVIN"
            type="text"
            placeholder="VIN-номер"
            @update:model-value="onFilterEdited"
          />
        </FormField>

        <FormField label="Поиск по клиентскому номеру телефона">
          <input
            v-model.trim="filters.queryPhoneNumber"
            type="text"
            placeholder="Номер телефона"
            @update:model-value="onFilterEdited"
          />
        </FormField>

        <FormField label="Статус проверки Автокод">
          <select
            v-model="filters.autocodeStatus"
            @change="onFilterEdited"
          >
            <option v-for="opt in statusOptions" :key="opt.value" :value="opt.value">
              {{ opt.text }}
            </option>
          </select>
        </FormField>

        <div class="filters-actions">
          <button class="btn" type="button" @click="onResetFilters">
            Сбросить
          </button>
        </div>
      </FormGrid>
    </FormCard>

    <FormCard>
      <CarsToolbar
        :currentPage="currentPage"
        :totalPages="totalPages"
        :columnOrder="tableState.columnOrder"
        :columnVisibility="tableState.columnVisibility"
        :disabled="isLoading"
        @pageChanged="onPageChanged"
        @columnOrderChanged="tableState.columnOrder = $event"
        @columnVisibilityChanged="tableState.columnVisibility = $event"
        @resetSettings="resetTableState"
      />

      <my-loader v-if="isLoading" />

      <div v-else-if="hasError && !cars.length" class="state-block error-block">
        Ошибка загрузки списка машин. Попробуйте позже.
      </div>

      <div v-else class="content">
        <div v-if="hasError && cars.length" class="inline-error">
          Не удалось обновить список. Показаны предыдущие данные.
        </div>

        <CarsTable
          :cars="cars"
          :isLoading="isLoading"
          :isRefreshing="isFetching"
          :isPlaceholderData="isPlaceholderData"
          :columnOrder="tableState.columnOrder"
          :columnVisibility="tableState.columnVisibility"
          :columnSizing="tableState.columnSizing"
          :pageCount="totalPages"
          :currentPage="currentPage"
          @columnSizingChanged="tableState.columnSizing = $event"
        />
      </div>
    </FormCard>
  </FormPage>
</template>

<script>
import { onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import FormPage from '@/components/formKit/FormPage.vue'
import FormCard from '@/components/formKit/FormCard.vue'
import FormGrid from '@/components/formKit/FormGrid.vue'
import FormField from '@/components/formKit/FormField.vue'
import CarsTable from './components/CarsTable.vue'
import CarsToolbar from './components/CarsToolbar.vue'
import { useCarsPage } from './useCarsPage'
import {
  carsFiltersEqual,
  filtersFromRouteQuery,
  filtersToRouteQuery,
  resolveInitialCarsFilters,
} from './carsState.mjs'

export default {
  name: 'CarList',
  components: {
    FormPage,
    FormCard,
    FormGrid,
    FormField,
    CarsTable,
    CarsToolbar,
  },
  setup() {
    const router = useRouter()
    const route = useRoute()

    const page = useCarsPage(
      resolveInitialCarsFilters(route.query, window.localStorage),
    )
    const {
      filters,
      tableState,
      cars,
      isLoading,
      isFetching,
      isPlaceholderData,
      hasError,
      totalPages,
      currentPage,
      setPage,
      replaceFilters,
      resetFilters,
      resetTableState,
    } = page

    const statusOptions = ref([
      { text: 'Все записи', value: 1 },
      { text: 'Проверено', value: 0 },
      { text: 'Не проверено', value: -1 },
    ])

    const syncUrl = () => {
      const nextQuery = filtersToRouteQuery(filters)
      const current = route.query || {}
      const same =
        String(current.query || '') === String(nextQuery.query || '')
        && String(current.queryCarNumber || '') === String(nextQuery.queryCarNumber || '')
        && String(current.queryVIN || '') === String(nextQuery.queryVIN || '')
        && String(current.queryPhoneNumber || '') === String(nextQuery.queryPhoneNumber || '')
        && String(current.autocodeStatus ?? '') === String(nextQuery.autocodeStatus ?? '')
        && String(current.page || '') === String(nextQuery.page || '')
      if (same) return
      router.replace({ query: nextQuery }).catch(() => {})
    }

    watch(
      () => ({ ...filters }),
      () => { syncUrl() },
      { deep: true },
    )

    // Apply URL → filters only on browser Back/Forward (popstate).
    // Capture phase runs before Vue Router's listener so the flag is set
    // before route.query updates and our watch runs.
    // router.replace from syncUrl does not fire popstate.
    let applyRouteFromHistory = false
    const onPopState = () => {
      applyRouteFromHistory = true
    }
    onMounted(() => window.addEventListener('popstate', onPopState, true))
    onUnmounted(() => window.removeEventListener('popstate', onPopState, true))

    watch(
      () => route.query,
      (query) => {
        if (!applyRouteFromHistory) return
        applyRouteFromHistory = false
        const fromRoute = filtersFromRouteQuery(query)
        if (carsFiltersEqual(fromRoute, filters)) return
        replaceFilters(fromRoute)
      },
    )

    const onFilterEdited = () => {
      filters.autocodeStatus = Number(filters.autocodeStatus)
      if (filters.page !== 1) filters.page = 1
    }

    const onPageChanged = (nextPage) => {
      setPage(nextPage)
    }

    const onResetFilters = () => {
      resetFilters()
    }

    return {
      router,
      filters,
      tableState,
      cars,
      isLoading,
      isFetching,
      isPlaceholderData,
      hasError,
      totalPages,
      currentPage,
      statusOptions,
      onFilterEdited,
      onPageChanged,
      onResetFilters,
      resetTableState,
    }
  },
}
</script>

<style scoped>
.btn {
  border: 1px solid #d7dce2;
  background: #fff;
  color: #333;
  cursor: pointer;
  font-size: 13px;
  height: 32px;
  border-radius: 4px;
  padding: 0 12px;
}

.btn:hover {
  border-color: #007bff;
  color: #007bff;
}

.btn-primary {
  background: #007bff;
  border-color: #007bff;
  color: #fff;
}

.btn-primary:hover {
  background: #0056b3;
  border-color: #0056b3;
  color: #fff;
}

.filters-actions {
  display: flex;
  align-items: flex-end;
}

.content {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: stretch;
  gap: 12px;
  font-size: 12px;
  width: 100%;
}

.state-block {
  padding: 32px;
  color: #667085;
  font-size: 14px;
}

.error-block {
  color: #c0392b;
}

.inline-error {
  width: 100%;
  padding: 8px 10px;
  border-radius: 4px;
  background: #fff4e5;
  color: #8a5a00;
  font-size: 13px;
}
</style>
